import { useCallback, useEffect, useRef, useState } from 'react';
import {
    HubConnection,
    HubConnectionBuilder,
    ILogger,
    LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';
import { useAxiosSwagger } from './useAxiosSwagger';
import { traceHubUrl } from '../api/baseUrl';
import { describeError } from '../api/requeue';
import {
    ObjectTrace,
    TraceData,
    TraceDataRef,
    TraceProgress,
    TraceRequest,
    TraceSearch,
    TraceStreamMessage,
    day,
    getTrace,
    getTraceData,
    parseDay,
} from '../api/trace';

/** The streaming method, exactly as it is named in TraceHub. */
const TRACE = 'trace';

/**
 * Logger that swallows the one message React's double-mounted effects always produce.
 *
 * Same reason as in useQueueMonitor: in development every effect runs twice, so the first
 * connection is aborted mid-negotiation and logs itself as a failure it is not.
 */
const quietLogger: ILogger = {
    log(logLevel, message) {
        if (
            logLevel < LogLevel.Warning ||
            message.includes('stopped during negotiation')
        ) {
            return;
        }

        // eslint-disable-next-line no-console
        console.warn(`[trace-hub] ${message}`);
    },
};

export interface TraceMonitor {
    /** What was found, or null when nothing has been asked yet. */
    trace: ObjectTrace | null;
    /** What is being traced, kept so the panel can put it in the search box. */
    request: TraceRequest | null;
    loading: boolean;
    error: string | null;
    /**
     * The steps of the running trace, oldest first, with the finished ones kept.
     *
     * Empty on the fallback path, which cannot report anything: a browser that reaches the
     * REST endpoint gets a spinner and no steps, and that is exactly what it used to get.
     */
    progress: TraceProgress[];
    /** Whether the steps are coming from the hub, so the panel can say why they are absent. */
    streaming: boolean;
    /** Whether the stack traces come along. Off until somebody asks for one. */
    stackTraces: boolean;
    open: (request: TraceRequest) => void;
    close: () => void;
    reload: () => void;
    showStackTraces: () => void;
    /**
     * Reads the days before the ones just searched, for the same id.
     *
     * Pressable again and again: each answer names the next chunk, so an id that turns up
     * nowhere can be walked back as far as the tables go without any single request having
     * to read months of day partitions.
     */
    searchFurtherBack: () => void;
    /** Whether there are older days to offer. */
    canSearchFurtherBack: boolean;
    /**
     * The rounds of "search further back" that turned up nothing new, oldest first.
     *
     * What keeps a find on screen. Walking back reads older days *only*, so a round that finds
     * nothing would otherwise replace an answer with "niets gevonden" - the object had not
     * moved, the panel had simply stopped looking where it was. Now the answer stays and the
     * rounds are listed as days that have also been read.
     */
    alsoSearched: TraceSearch[];
    /** Abandons a running trace without losing what is already on screen. */
    cancel: () => void;
    /**
     * One stored body, fetched because somebody opened it.
     *
     * Not part of the trace and not held in state: these bodies are tens of kilobytes each
     * and one trace can point at a dozen of them, so each one is fetched on the way to the
     * component that shows it and forgotten when it closes.
     */
    readData: (reference: TraceDataRef) => Promise<TraceData>;
}

/**
 * Follows one object, on demand.
 *
 * Deliberately not polling, unlike the health and queue panels next to it. Those describe a
 * moving environment; this describes one object's history, and a history that redraws itself
 * every ten seconds is one you cannot read a stack trace out of. It refetches when you ask
 * for a different object, when you press reload, and when you open a stack trace - nothing
 * else moves it.
 *
 * A trace is streamed over the hub rather than fetched, because it can read sixty-two day
 * partitions of a table with tens of thousands of rows in each. Behind a plain spinner that
 * is indistinguishable from a hung request; over the stream the panel can say which week is
 * being read and how long it has been at it. The REST call stays as the fallback for a
 * browser or a proxy that cannot hold a websocket open.
 */
export const useTrace = (
    customer?: string,
    environment?: string
): TraceMonitor => {
    const axios = useAxiosSwagger();
    const { getAccessTokenSilently } = useAuth();

    const [trace, setTrace] = useState<ObjectTrace | null>(null);
    const [request, setRequest] = useState<TraceRequest | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<TraceProgress[]>([]);
    const [streaming, setStreaming] = useState<boolean>(false);
    const [stackTraces, setStackTraces] = useState<boolean>(false);
    const [alsoSearched, setAlsoSearched] = useState<TraceSearch[]>([]);

    const mounted = useRef<boolean>(true);

    /** Whether the running fetch is a round of walking backwards rather than a new question. */
    const walkingBack = useRef<boolean>(false);

    /** What is on screen, so an arriving trace can be weighed against it outside a setter. */
    const shown = useRef<ObjectTrace | null>(null);

    /** The request the trace on screen came from, so a reload asks for that one again. */
    const shownRequest = useRef<TraceRequest | null>(null);

    /*
        Every fetch is stamped, and only the newest stamp is allowed to write. Clicking
        through four rows in a row otherwise leaves whichever request happened to finish
        last on screen, which is a trace of the wrong object under the right heading.
    */
    const round = useRef<number>(0);

    /** The hub of the trace that is running now, so it can be stopped. */
    const activeHub = useRef<HubConnection | null>(null);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
            activeHub.current?.stop().catch(() => undefined);
        };
    }, []);

    // A trace is about one environment. Switching environments has to clear it rather than
    // leave an object from somewhere else standing.
    useEffect(() => {
        setTrace(null);
        setRequest(null);
        setError(null);
        setProgress([]);
        setStackTraces(false);
        setAlsoSearched([]);

        walkingBack.current = false;
        shown.current = null;
        shownRequest.current = null;
    }, [customer, environment]);

    /**
     * Puts a finished trace on screen, or keeps the one that is already there.
     *
     * The one case where a fresh answer is the wrong answer. "Zoek verder terug" reads the days
     * before the ones just read and nothing else, so a round that comes up empty says something
     * about those days - not about the object. Replacing a find with it would take the webhook
     * that was just dug up off the screen and put "er is niets gevonden" in its place, which is
     * how a reader ends up believing the object moved.
     */
    const applyTrace = useCallback((next: ObjectTrace, from: TraceRequest) => {
        const back = walkingBack.current;

        walkingBack.current = false;

        if (back && !next.subject && shown.current?.subject) {
            setAlsoSearched((rounds) => [...rounds, next.search]);

            return;
        }

        shown.current = next;
        shownRequest.current = from;

        setTrace(next);
    }, []);

    useEffect(() => {
        if (!axios || !customer || !environment || !request) {
            return;
        }

        const mine = ++round.current;
        const mineNow = () => mounted.current && mine === round.current;

        // The previous trace is abandoned rather than left to finish. It would only arrive
        // late, under the heading of an object nobody is looking at any more, and it keeps
        // reading day partitions on the server until it does.
        activeHub.current?.stop().catch(() => undefined);
        activeHub.current = null;

        setLoading(true);
        setError(null);
        setProgress([]);

        /**
         * Keeps one line per step rather than one per message.
         *
         * Every message is a complete statement of one step, so the newest of a phase
         * replaces the older one and the order of first appearance is preserved. That is
         * what makes a finished step stay on screen with its result instead of scrolling
         * away under the running one.
         */
        const applyProgress = (message: TraceProgress) =>
            setProgress((current) => {
                const next = [...current];
                const at = next.findIndex((step) => step.phase === message.phase);

                if (at < 0) {
                    next.push(message);
                } else {
                    next[at] = message;
                }

                return next;
            });

        /** The REST endpoint: the same trace, without a word about how it is going. */
        const fetchWithoutProgress = async () => {
            try {
                const result = await getTrace(axios, customer, environment, {
                    ...request,
                    stackTraces,
                });

                if (mineNow()) {
                    applyTrace(result, request);
                }
            } catch (e) {
                if (mineNow()) {
                    setError(describeError(e));
                }
            } finally {
                if (mineNow()) {
                    setLoading(false);
                    setStreaming(false);
                }
            }
        };

        const run = async () => {
            let hub: HubConnection | null = null;

            try {
                hub = new HubConnectionBuilder()
                    .withUrl(traceHubUrl, {
                        accessTokenFactory: () => getAccessTokenSilently(),
                    })
                    .configureLogging(quietLogger)
                    .build();

                await hub.start();
            } catch {
                // No websocket, no negotiate, no token: none of it is worth an error on
                // screen while a plain GET can still answer the question.
                await hub?.stop().catch(() => undefined);

                if (mineNow()) {
                    setStreaming(false);
                }

                await fetchWithoutProgress();

                return;
            }

            if (!mineNow()) {
                await hub.stop().catch(() => undefined);

                return;
            }

            activeHub.current = hub;
            setStreaming(true);

            let answered = false;

            await new Promise<void>((resolve) => {
                hub!
                    .stream<TraceStreamMessage>(
                        TRACE,
                        customer,
                        environment,
                        request.id ?? null,
                        request.objectType ?? null,
                        request.relationId ?? null,
                        request.from ? day(request.from) : null,
                        request.to ? day(request.to) : null,
                        stackTraces
                    )
                    .subscribe({
                        next: (message) => {
                            if (!mineNow()) {
                                return;
                            }

                            if (message.progress) {
                                applyProgress(message.progress);
                            }

                            if (message.trace) {
                                answered = true;
                                applyTrace(message.trace, request);
                            }
                        },
                        error: (e: unknown) => {
                            if (mineNow()) {
                                setError(describeError(e));
                            }

                            resolve();
                        },
                        complete: () => resolve(),
                    });
            });

            await hub.stop().catch(() => undefined);

            if (activeHub.current === hub) {
                activeHub.current = null;
            }

            if (!mineNow()) {
                return;
            }

            setLoading(false);

            // A stream that closed without ever carrying a trace is a stream that broke.
            // Falling back keeps the panel useful instead of leaving it empty with a row of
            // finished steps above it.
            if (!answered) {
                setStreaming(false);
                setLoading(true);

                await fetchWithoutProgress();
            }
        };

        void run();
    }, [
        axios,
        customer,
        environment,
        request,
        stackTraces,
        getAccessTokenSilently,
        applyTrace,
    ]);

    const open = useCallback((next: TraceRequest) => {
        setStackTraces(false);
        setAlsoSearched([]);

        walkingBack.current = false;
        shown.current = null;
        shownRequest.current = null;

        // A new object rather than the same one twice, so asking for the object already on
        // screen still counts as a fresh request and refetches.
        setRequest({ ...next });
    }, []);

    const close = useCallback(() => {
        round.current++;
        activeHub.current?.stop().catch(() => undefined);
        activeHub.current = null;

        setRequest(null);
        setTrace(null);
        setError(null);
        setProgress([]);
        setLoading(false);
        setStackTraces(false);
        setAlsoSearched([]);

        walkingBack.current = false;
        shown.current = null;
        shownRequest.current = null;
    }, []);

    const cancel = useCallback(() => {
        round.current++;
        activeHub.current?.stop().catch(() => undefined);
        activeHub.current = null;

        setLoading(false);
    }, []);

    /*
        Asks for the days the trace on screen came from, not the days that were read last.

        Those are two different things once somebody has walked backwards: the last round read
        older days and found nothing, so repeating it would reload an empty answer over a find.
    */
    const reload = useCallback(() => {
        walkingBack.current = false;

        setRequest((current) => {
            const target = shownRequest.current ?? current;

            return target ? { ...target } : current;
        });
    }, []);

    const showStackTraces = useCallback(() => setStackTraces(true), []);

    const readData = useCallback(
        async (reference: TraceDataRef): Promise<TraceData> => {
            if (!axios || !customer || !environment) {
                throw new Error(
                    'Er is geen omgeving gekozen, dus er valt niets op te halen.'
                );
            }

            return getTraceData(axios, customer, environment, reference);
        },
        [axios, customer, environment]
    );

    /*
        The chunk to read next comes from the last round, not from the trace on screen.

        After a round that found nothing the two are different: the trace still shows the older
        find, while the days left to read are the ones before the round that just came back
        empty. Taking the chunk off the trace would walk the same days over and over.
    */
    const lastRound =
        alsoSearched.length > 0
            ? alsoSearched[alsoSearched.length - 1]
            : trace?.search;

    const nextFrom = parseDay(lastRound?.nextFrom);
    const nextTo = parseDay(lastRound?.nextTo);

    const searchFurtherBack = useCallback(() => {
        if (!nextFrom || !nextTo) {
            return;
        }

        walkingBack.current = true;

        // The type and the relation id go along untouched. Walking back is about which days
        // are read, not about which object is being looked for - and dropping them here would
        // turn "keep looking for this invoice" into "resolve this id again, from scratch".
        setRequest((current) =>
            current ? { ...current, from: nextFrom, to: nextTo } : current
        );
    }, [nextFrom, nextTo]);

    return {
        trace,
        request,
        loading,
        error,
        progress,
        streaming,
        stackTraces,
        open,
        close,
        reload,
        showStackTraces,
        searchFurtherBack,
        canSearchFurtherBack: !!nextFrom && !!nextTo,
        alsoSearched,
        cancel,
        readData,
    };
};
